import { useEffect, useState } from "react";
import { KeyRound, RefreshCw, ChevronRight, CheckSquare, Square, FileSpreadsheet, Library, ClipboardList, FlaskConical, FileBarChart2, Users, BadgeCheck, Layers, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  adminListAttempts,
  adminGetAttempt,
  adminToggleApprove,
  adminDeleteAttempt,
  adminToken,
  adminLogin,
  adminAttemptsXlsxUrl,
  attemptPdfUrl,
  certificateUrl,
} from "@/lib/api";
import { shortId, attemptPdfFilename } from "@/lib/ids";
import DomainBadge from "@/components/DomainBadge";
import DownloadButton from "@/components/DownloadButton";
import QuestionBank from "@/components/admin/QuestionBank";
import ConceptConfig from "@/components/admin/ConceptConfig";
import EnginePreview from "@/components/admin/EnginePreview";
import ResearchExport from "@/components/admin/ResearchExport";
import DatabaseBrowser from "@/components/admin/DatabaseBrowser";
import ScenarioBank from "@/components/admin/ScenarioBank";
import AssignmentSets from "@/components/admin/AssignmentSets";
import { ConceptReview } from "@/components/admin/ConceptReview";
import { BehaviourLog } from "@/components/admin/BehaviourLog";
import { useT } from "@/lib/i18n";

export default function Admin() {
  const { t } = useT();
  const [authed, setAuthed] = useState(!!adminToken.get());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  // attempts | research | bank | contracts | engine | candidates | licenses
  const [view, setView] = useState("attempts");
  const [attempts, setAttempts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminListAttempts();
      setAttempts(r.attempts || []);
    } catch (e) {
      const msg = e?.response?.status === 401 ? t("admin.invalid_token") : (e.message || "Error");
      setErr(msg);
      if (e?.response?.status === 401) setAuthed(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);
  useEffect(() => {
    if (!selected) return;
    adminGetAttempt(selected).then(setDetail).catch((e) => toast.error(e.message));
  }, [selected]);

  if (!authed) {
    const doLogin = async () => {
      setLoginBusy(true); setLoginErr(null);
      try {
        const r = await adminLogin(username.trim(), password);
        adminToken.set(r.token);
        setAuthed(true);
      } catch (e) {
        const status = e?.response?.status;
        const msg = status === 401
          ? t("admin.invalid_credentials")
          : (e?.response?.data?.detail || e.message || "Login failed");
        setLoginErr(msg);
      } finally {
        setLoginBusy(false);
      }
    };
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="admin-gate">
        <div className="label-mono">{t("admin.gate_label")}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-2">{t("admin.gate_title")}</h1>

        <div className="mt-8 grid gap-4">
          <div>
            <Label htmlFor="u" className="label-mono">{t("admin.username_label")}</Label>
            <Input
              id="u"
              type="text"
              autoComplete="username"
              data-testid="input-admin-username"
              className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t("admin.username_ph")}
              onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }}
            />
          </div>
          <div>
            <Label htmlFor="p" className="label-mono">{t("admin.password_label")}</Label>
            <Input
              id="p"
              type="password"
              autoComplete="current-password"
              data-testid="input-admin-password"
              className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("admin.password_ph")}
              onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }}
            />
          </div>
          {loginErr && (
            <div className="text-sm text-rose-600" data-testid="admin-login-error">
              {loginErr}
            </div>
          )}
          <Button
            data-testid="btn-admin-signin"
            className="btn-ink rounded-none h-11"
            onClick={doLogin}
            disabled={loginBusy || !username || !password}
          >
            <KeyRound size={14} className="mr-2" />
            {loginBusy ? t("admin.signing_in") : t("admin.btn_signin")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${view === "attempts" ? "max-w-[1760px]" : "max-w-7xl"} mx-auto px-6 md:px-12 py-10`} data-testid="admin-page">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <div className="label-mono">{t("admin.list_label")}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {view === "attempts" ? t("admin.list_title")
              : view === "research" ? t("admin.research_title")
              : view === "bank" ? t("admin.bank_title")
              : view === "scenariobank" ? "Assessment Scenario Bank"
              : view === "assignmentsets" ? "Assignment Sets"
              : view === "contracts" ? t("admin.contracts_title")
              : view === "engine" ? t("admin.engine_title")
              : view === "candidates" ? t("admin.tab_candidates")
              : view === "licenses" ? t("admin.tab_licenses")
              : ""}
          </h1>
        </div>
        <div className="ms-auto flex gap-2 flex-wrap items-center">
          <div className="inline-flex border border-[var(--iddl-rule)] flex-wrap" role="tablist" data-testid="admin-tabs">
            <button
              type="button"
              data-testid="tab-attempts"
              onClick={() => setView("attempts")}
              className={`px-3 py-2 text-sm font-medium transition ${view === "attempts" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
            >
              {t("admin.tab_attempts")}
            </button>
            <button
              type="button"
              data-testid="tab-research"
              onClick={() => setView("research")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "research" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="Behaviour event streams, timing & sequence analysis"
            >
              <FileBarChart2 size={14} className="inline mr-2" />
              {t("admin.tab_research")}
            </button>
            <button
              type="button"
              data-testid="tab-bank"
              onClick={() => setView("bank")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "bank" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="MCQs, answer options, knowledge scoring"
            >
              <Library size={14} className="inline mr-2" />
              {t("admin.tab_bank")}
            </button>
            <button
              type="button"
              data-testid="tab-scenariobank"
              onClick={() => setView("scenariobank")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "scenariobank" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="Phase 10 · Assessment Scenario Bank — variants per concept"
            >
              <Library size={14} className="inline mr-2" />
              Scenario Bank
            </button>
            <button
              type="button"
              data-testid="tab-assignmentsets"
              onClick={() => setView("assignmentsets")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "assignmentsets" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="12 predefined 3-concept sets · assign/unassign only"
            >
              <Layers size={14} className="inline mr-2" />
              Assignment Sets
            </button>
            <button
              type="button"
              data-testid="tab-contracts"
              onClick={() => setView("contracts")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "contracts" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="Behavioural tasks, required/critical/auto-fail events, scoring contracts, remediation"
            >
              <ClipboardList size={14} className="inline mr-2" />
              {t("admin.tab_contracts")}
            </button>
            <button
              type="button"
              data-testid="tab-engine"
              onClick={() => setView("engine")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "engine" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
              title="Live behavioural simulator preview"
            >
              <FlaskConical size={14} className="inline mr-2" />
              {t("admin.tab_engine")}
            </button>
            <button
              type="button"
              data-testid="tab-candidates"
              onClick={() => setView("candidates")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "candidates" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
            >
              <Users size={14} className="inline mr-2" />
              {t("admin.tab_candidates")}
            </button>
            <button
              type="button"
              data-testid="tab-licenses"
              onClick={() => setView("licenses")}
              className={`px-3 py-2 text-sm font-medium transition border-l border-[var(--iddl-rule)] ${view === "licenses" ? "btn-ink" : "bg-transparent hover:bg-[var(--iddl-surface)]"}`}
            >
              <BadgeCheck size={14} className="inline mr-2" />
              {t("admin.tab_licenses")}
            </button>
          </div>
          {view === "attempts" && (
            <>
              <DownloadButton
                url={`${adminAttemptsXlsxUrl()}?admin_token=${encodeURIComponent(adminToken.get())}`}
                filename="IDDL_All_Attempts.xlsx"
                label={t("admin.download_all_attempts_xlsx")}
                icon={<FileSpreadsheet size={14} className="ml-2" />}
                data-testid="btn-admin-download-attempts-xlsx"
              />
              <Button variant="outline" data-testid="btn-admin-refresh" onClick={load} className="rounded-none">
                <RefreshCw size={14} className="mr-2" /> {t("admin.refresh")}
              </Button>
            </>
          )}
          <Button variant="outline" className="rounded-none" onClick={() => { adminToken.clear(); setAuthed(false); }} data-testid="btn-admin-signout">
            {t("admin.signout")}
          </Button>
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-[var(--iddl-cs)]">{err}</div>}

      {view === "research" ? (
        <ResearchExport />
      ) : view === "scenariobank" ? (
        <ScenarioBank />
      ) : view === "assignmentsets" ? (
        <AssignmentSets />
      ) : view === "bank" ? (
        <QuestionBank />
      ) : view === "contracts" ? (
        <ConceptConfig />
      ) : view === "engine" ? (
        <EnginePreview />
      ) : view === "candidates" ? (
        <DatabaseBrowser key="candidates" collection="candidates" title={t("admin.tab_candidates")} />
      ) : view === "licenses" ? (
        <DatabaseBrowser key="licenses" collection="licenses" title={t("admin.tab_licenses")} />
      ) : (
      <div className="grid lg:grid-cols-12 gap-0 mt-8 border border-[var(--iddl-rule)]">
        <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[var(--iddl-rule)] max-h-[75vh] overflow-y-auto">
          {loading && <div className="p-4 label-mono">{t("admin.loading")}</div>}
          {!loading && attempts.length === 0 && <div className="p-4 text-sm text-[var(--iddl-muted)]">{t("admin.no_attempts")}</div>}
          {attempts.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a.id)}
              data-testid={`row-${a.id}`}
              className={`w-full text-left p-4 border-b border-[var(--iddl-rule)] hover:bg-[var(--iddl-surface)] ${selected === a.id ? "bg-[var(--iddl-surface)]" : ""}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold">{a.candidate_name}</span>
                <span className="label-mono">{shortId(a.id)}</span>
                {a.overall_outcome && (
                  <span className={`label-mono px-2 py-0.5 border ${a.overall_outcome === "Fail" ? "tint-cs" : a.overall_outcome === "Merit" ? "tint-ds" : "tint-ai"}`}>
                    {a.overall_outcome}
                  </span>
                )}
                {a.admin_approved === true && <span className="label-mono tint-ds px-2 py-0.5 border">{t("admin.approved")}</span>}
                <ChevronRight size={14} className="ms-auto opacity-50" />
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-[var(--iddl-muted)]">
                {a.sequence_ids?.map((cid) => <code key={cid} className="font-mono-disp">{cid}</code>)}
              </div>
              <div className="text-[11px] text-[var(--iddl-muted)] mt-1">
                {a.candidate_email || "—"} · {t("admin.started")} {fmt(a.started_at)} · {t("admin.step")} {a.current_step}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8 p-6 max-h-[75vh] overflow-y-auto" data-testid="admin-detail">
          {!detail && <div className="text-sm text-[var(--iddl-muted)]">{t("admin.select_attempt")}</div>}
          {detail && <AttemptDetail
            t={t}
            detail={detail}
            onApprove={async () => {
              await adminToggleApprove(detail.id);
              toast.success(t("admin.approval_toggled"));
              const fresh = await adminGetAttempt(detail.id);
              setDetail(fresh); load();
            }}
            onDelete={async () => {
              const who = detail.candidate_name || detail.id;
              if (!window.confirm(t("admin.confirm_delete_attempt").replace("{name}", who))) return;
              try {
                const r = await adminDeleteAttempt(detail.id);
                toast.success(
                  r.license_deleted
                    ? t("admin.deleted_with_license")
                    : t("admin.deleted")
                );
                setSelected(null);
                setDetail(null);
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || e.message || "Delete failed");
              }
            }}
          />}
        </div>
      </div>
      )}
    </div>
  );
}

function AttemptDetail({ t, detail, onApprove, onDelete }) {
  // Two separate downloads:
  //   • Attempt PDF      → /attempts/{id}/attempt_pdf  (red "ATTEMPT NO." record)
  //   • Licence PDF      → /license/{lid}/certificate  (gold "LICENCE NO." cert)
  // The licence button only renders when a licence has actually been issued
  // (which now happens for Pass / Merit / Fail per the updated policy).
  const attemptUrl = attemptPdfUrl(detail.id);
  const attemptFilename = attemptPdfFilename(detail.id);
  const licUrl = detail.license_id ? certificateUrl(detail.license_id) : null;
  const licFilename = detail.license_id ? `${detail.license_id}.pdf` : null;
  return (
    <div data-testid="attempt-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-display text-2xl font-bold">{detail.candidate_name}</h2>
        <code className="font-mono-disp text-sm">{detail.id}</code>
        {detail.overall_outcome && (
          <span className={`label-mono px-2 py-0.5 border ${detail.overall_outcome === "Fail" ? "tint-cs" : detail.overall_outcome === "Merit" ? "tint-ds" : "tint-ai"}`}>
            {detail.overall_outcome}
          </span>
        )}
      </div>
      <div className="mt-1 text-sm text-[var(--iddl-muted)]">
        {detail.candidate_email || "—"}
        {" · "}{t("admin.started")} {fmt(detail.started_at)}
        {detail.completed_at && (
          <> {" · "}{t("admin.completed")} {fmt(detail.completed_at)}
            {" · "}{t("result.duration")} {durationOf(detail.started_at, detail.completed_at)}
          </>
        )}
        {!detail.completed_at && <> {" · "}{t("admin.in_progress")}</>}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <Button onClick={onApprove} variant="outline" className="rounded-none" data-testid="btn-toggle-approve">
          {detail.admin_approved ? <CheckSquare size={14} className="mr-2" /> : <Square size={14} className="mr-2" />}
          {detail.admin_approved ? t("admin.btn_revoke") : t("admin.btn_approve")}
        </Button>
        {detail.current_step === "complete" && (
          <DownloadButton
            url={attemptUrl}
            filename={attemptFilename}
            label={t("admin.download_attempt_pdf")}
            data-testid="btn-admin-download-attempt-pdf"
          />
        )}
        {detail.current_step === "complete" && licUrl && (
          <DownloadButton
            url={licUrl}
            filename={licFilename}
            label={t("admin.download_license_pdf")}
            data-testid="btn-admin-download-license-pdf"
            className="bg-amber-600 hover:bg-amber-700 text-white border-amber-700"
          />
        )}
        <Button
          onClick={onDelete}
          variant="outline"
          className="rounded-none border-rose-600 text-rose-600 hover:bg-rose-50"
          data-testid="btn-admin-delete-attempt"
        >
          <Trash2 size={14} className="mr-2" />
          {t("admin.btn_delete")}
        </Button>
      </div>

      <h3 className="font-display text-xl font-semibold mt-8">{t("admin.scoring_title")}</h3>
      <p className="text-sm text-[var(--iddl-muted)] mt-1">{t("admin.scoring_sub")}</p>

      <div className="mt-4 grid gap-3">
        {(detail.concept_results || []).map((r, i) => r && (
          <div key={i} className="paper p-5" data-testid={`concept-card-${r.concept_id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <DomainBadge domain={r.domain} />
              <span className="font-display font-semibold">{r.title}</span>
              <code className="label-mono">{r.concept_id}</code>
              {r.outcome && (
                <span className={`label-mono px-2 py-0.5 border ms-auto ${r.outcome === "Fail" ? "tint-cs" : r.outcome === "Merit" ? "tint-ds" : "tint-ai"}`}>
                  {r.outcome}
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-3 mt-4 border border-[var(--iddl-rule)]">
              <LayerCell t={t} title={t("result.know")} layer={r.knowledge} />
              <LayerCell t={t} title={t("result.perform")} layer={r.capability} extra={r.capability_evidence} />
              <LayerCell t={t} title={t("result.reason")} layer={r.reflection} />
            </div>

            <ConceptReview title={t("admin.review_title")} r={r} />

            <BehaviourLog cid={r.concept_id} events={detail.behaviour_log?.[r.concept_id] || []} />
          </div>
        ))}
        {(detail.sequence_ids || [])
          .filter((cid) => !(detail.concept_results || []).some((r) => r?.concept_id === cid))
          .map((cid) => (
            <div key={cid} className="paper p-5" data-testid={`concept-card-pending-${cid}`}>
              <div className="flex items-center gap-3">
                <code className="label-mono">{cid}</code>
                <span className="label-mono ms-auto text-[var(--iddl-muted)]">{t("admin.pending")}</span>
              </div>
              <BehaviourLog cid={cid} events={detail.behaviour_log?.[cid] || []} />
            </div>
          ))}
      </div>
    </div>
  );
}

function LayerCell({ t, title, layer, extra }) {
  return (
    <div className="p-4 border-r border-b border-[var(--iddl-rule)] last:border-r-0">
      <div className="label-mono">{title}</div>
      {layer ? (
        <>
          <div className="font-display text-xl font-semibold mt-1">{layer.score}/{layer.max_score}</div>
          <div className={`label-mono mt-1 ${layer.outcome === "Fail" ? "text-cs" : layer.outcome === "Merit" ? "text-ds" : "text-ai"}`}>{layer.outcome}</div>
          {layer.detail && <div className="text-xs text-[var(--iddl-muted)] mt-1">{layer.detail}</div>}
          {extra?.auto_fail_triggered?.length > 0 && (
            <div className="text-xs text-cs mt-1">auto-fail: {extra.auto_fail_triggered.join(", ")}</div>
          )}
        </>
      ) : (
        <div className="label-mono mt-2 text-[var(--iddl-muted)]">{t("admin.pending")}</div>
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toUTCString().slice(5, 22); } catch { return iso; }
}

function durationOf(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  try {
    const ms = Math.max(0, new Date(endIso) - new Date(startIso));
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  } catch { return "—"; }
}
